import { Vector3 } from 'three';

import { isDefined } from '../../../utils/variables';

/**
 * Fills in the missing values of a line or polygon by interpolating between the closest values
 * before and after, based on the distance along the line. At the ends of a line, where there is a
 * value on one side only, the closest value is used.
 *
 * @param values A value for each position, undefined where it is missing.
 * @param positions The positions of the line or polygon.
 * @returns The values with the missing ones filled in, or undefined if all values are missing.
 */
export const interpolateMissingValues = (
  values: (number | undefined)[],
  positions: Vector3[],
  isPolygon: boolean,
): number[] | undefined => {
  const count = values.length;

  if (!values.some(isDefined)) {
    return undefined;
  }

  // The distance along the line to each position, and the length of the whole polygon.
  const distances = [0];
  for (let i = 1; i < count; i++) {
    distances.push(distances[i - 1] + positions[i].distanceTo(positions[i - 1]));
  }
  const perimeter = distances[count - 1] + positions[0].distanceTo(positions[count - 1]);

  /**
   * The distance along the line from one index to another, in the direction of increasing index.
   * For polygons it wraps around past the last position when the index decreases.
   */
  const distanceBetween = (from: number, to: number) =>
    to >= from ? distances[to] - distances[from] : distances[to] - distances[from] + perimeter;

  /**
   * Finds the closest index with a value in the given direction, wrapping around for polygons.
   */
  const findValueIndex = (index: number, step: 1 | -1) => {
    for (let i = 1; i < count; i++) {
      const next = index + step * i;

      if (!isPolygon && (next < 0 || next >= count)) {
        return undefined;
      }

      const wrapped = (next + count) % count;
      if (isDefined(values[wrapped])) {
        return wrapped;
      }
    }

    return undefined;
  };

  return values.map((value, index) => {
    if (isDefined(value)) {
      return value;
    }

    const previous = findValueIndex(index, -1);
    const next = findValueIndex(index, 1);

    if (!isDefined(previous) || !isDefined(next)) {
      return values[(previous ?? next)!]!;
    }

    const distanceFromPrevious = distanceBetween(previous, index);
    const distanceToNext = distanceBetween(index, next);
    const total = distanceFromPrevious + distanceToNext;
    const t = total > 0 ? distanceFromPrevious / total : 0;

    return values[previous]! + (values[next]! - values[previous]!) * t;
  });
};
